import Vue from 'vue';
import { debounce, throttle } from 'lodash';
import { trackEventApi } from '~/common/utils/track';
// 可配置的埋点参数
const TRACK_CONFIG = {
  clickThrottle: 300, // 点击节流时间
  exposureDebounce: 300, // 曝光防抖时间
  exposureThreshold: 0.5, // 曝光阈值
  reportMultipleExposures: false // 是否上报多次曝光
};
// 检查IntersectionObserver兼容性
const hasIntersectionObserver = () => 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window;

Vue.directive('track', {
  inserted(el, binding, vnode) {
    if (!binding.value || binding.value === 'none') return;
    initializeTracking(el, binding, vnode);
  },
  // 处理组件更新的情况
  componentUpdated(el, binding, vnode) {
    if (!binding.value) return;
    // 如果埋点数据变化，重新初始化埋点
    if (JSON.stringify(binding.oldValue) !== JSON.stringify(binding.value)) {
      cleanupTracking(el);
      initializeTracking(el, binding, vnode);
    }
  },
  unbind(el) {
    cleanupTracking(el);
  }
});

// 初始化埋点处理
function initializeTracking(el, binding, vnode) {
  //eventType事件名
  const eventType = binding.arg;
  const vm = vnode.context;
  const value = binding.value;
  const $route = vm.$route;

  // 点击埋点（使用节流）
  if (eventType === 'click') {
    const sendClick = throttle(async (e) => {
        await trackEventApi({ $route, value });
    }, TRACK_CONFIG.clickThrottle);
    el.addEventListener('click', sendClick);
    el._track_click = sendClick;
  }
  // 曝光埋点
  if (eventType === 'exposure') {
    // 降级处理：不支持IntersectionObserver的环境
    if (!hasIntersectionObserver()) {
      console.warn('当前环境不支持IntersectionObserver，使用降级方案');
      // 简单的降级处理：页面加载后延迟一段时间上报
      setTimeout(async () => {
        if (isElementVisible(el)) {
          await trackEventApi({ $route, value });
        }
      }, 1000);
      return;
    }

    const sendExposure = debounce(async () => {
      await trackEventApi({ $route, value });
      // 是否需要继续观察（支持多次曝光）
      if (!TRACK_CONFIG.reportMultipleExposures) {
        observer.unobserve(el);
      }
    }, TRACK_CONFIG.exposureDebounce);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= TRACK_CONFIG.exposureThreshold) {
            sendExposure();
          }
        });
      },
      {
        threshold: TRACK_CONFIG.exposureThreshold
      }
    );

    observer.observe(el);
    el._track_observer = observer;
    el._track_exposure = sendExposure;
  }
}

// 清理埋点相关资源
function cleanupTracking(el) {
  if (el?._track_click) {
    el._track_click.cancel?.();
    el.removeEventListener('click', el._track_click);
    delete el._track_click;
  }

  if (el?._track_exposure) {
    el._track_exposure?.cancel?.();
    delete el._track_exposure;
  }

  if (el?._track_observer) {
    el._track_observer?.disconnect?.();
    delete el._track_observer;
  }
}

// 判断元素是否在可视区域（降级方案使用）
function isElementVisible(el) {
  const rect = el.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
}
